import { Component } from '@theme/component';
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { DrawerOpenEvent, DrawerCloseEvent } from '@theme/theme-drawer';

/**
 * Header actions component that manages cart notifications and the
 * cart-drawer trigger's `aria-expanded` state.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} liveRegion - The live region for cart announcements.
 *
 * @extends {Component<Refs>}
 */
class HeaderActions extends Component {
  requiredRefs = ['liveRegion'];
  // Keep the account flyover open briefly so the pointer can cross adjacent header actions.
  accountMenuCloseDelay = 250;

  connectedCallback() {
    super.connectedCallback();
    this.accountMenus = [...this.querySelectorAll('.account-button__menu')];
    this.accountMenuHandlers = [];

    for (const menu of this.accountMenus) {
      const trigger = menu.querySelector('.account-button__menu-trigger');
      const menuState = {
        menu,
        trigger,
        closeTimer: null,
      };
      const onMouseEnter = () => this.openAccountMenu(menuState);
      const onMouseLeave = () => this.queueAccountMenuClose(menuState);
      const onFocusIn = () => this.openAccountMenu(menuState);
      const onFocusOut = (event) => {
        const relatedTarget = /** @type {Node | null} */ (event.relatedTarget);
        if (relatedTarget instanceof Node && menu.contains(relatedTarget)) return;
        this.closeAccountMenu(menuState);
      };

      menu.addEventListener('mouseenter', onMouseEnter);
      menu.addEventListener('mouseleave', onMouseLeave);
      menu.addEventListener('focusin', onFocusIn);
      menu.addEventListener('focusout', onFocusOut);

      this.accountMenuHandlers.push({
        ...menuState,
        onMouseEnter,
        onMouseLeave,
        onFocusIn,
        onFocusOut,
      });
    }

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
    this.#syncCartTriggerAriaExpanded();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    for (const menuState of this.accountMenuHandlers ?? []) {
      this.clearAccountMenuCloseTimer(menuState);
      const { menu, onMouseEnter, onMouseLeave, onFocusIn, onFocusOut } = menuState;
      menu.removeEventListener('mouseenter', onMouseEnter);
      menu.removeEventListener('mouseleave', onMouseLeave);
      menu.removeEventListener('focusin', onFocusIn);
      menu.removeEventListener('focusout', onFocusOut);
    }

    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    document.removeEventListener(DrawerOpenEvent.eventName, this.#onDrawerStateChange);
    document.removeEventListener(DrawerCloseEvent.eventName, this.#onDrawerStateChange);
  }

  clearAccountMenuCloseTimer(menuState) {
    if (menuState.closeTimer) {
      clearTimeout(menuState.closeTimer);
      menuState.closeTimer = null;
    }
  }

  openAccountMenu(menuState) {
    this.clearAccountMenuCloseTimer(menuState);
    menuState.menu?.setAttribute('data-account-menu-open', '');
    if (menuState.trigger instanceof HTMLElement) {
      menuState.trigger.setAttribute('aria-expanded', 'true');
    }
  }

  queueAccountMenuClose(menuState) {
    this.clearAccountMenuCloseTimer(menuState);
    menuState.closeTimer = setTimeout(() => {
      this.closeAccountMenu(menuState);
    }, this.accountMenuCloseDelay);
  }

  closeAccountMenu(menuState) {
    this.clearAccountMenuCloseTimer(menuState);
    menuState.menu?.removeAttribute('data-account-menu-open');
    if (menuState.trigger instanceof HTMLElement) {
      menuState.trigger.setAttribute('aria-expanded', 'false');
    }
  }

  #syncCartTriggerAriaExpanded = () => {
    const cartDrawer = document.getElementById('cart-drawer');
    if (!cartDrawer) return;
    const trigger = this.querySelector('[aria-controls="cart-drawer"]');
    if (!trigger) return;
    trigger.setAttribute('aria-expanded', cartDrawer.hasAttribute('open') ? 'true' : 'false');
  };

  /**
   * Syncs `aria-expanded` on the cart-drawer trigger when the drawer opens or closes.
   * @param {Event} event
   */
  #onDrawerStateChange = (event) => {
    const target = /** @type {HTMLElement | null} */ (event.target);
    if (target?.id !== 'cart-drawer') return;
    this.#syncCartTriggerAriaExpanded();
  };

  /**
   * Handles cart update events and announces the new count to screen readers.
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    event.promise
      ?.then(({ cart }) => {
        const cartCount = cart?.totalQuantity;
        if (cartCount === undefined) return;

        this.refs.liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[header-actions] Event promise rejected:', error);
      });
  };
}

if (!customElements.get('header-actions')) {
  customElements.define('header-actions', HeaderActions);
}
